<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { workOrderStore } from '../../stores/work-order';
  import { projectStore } from '../../stores/project';
  import { worker } from '../../stores/worker';

  export let existingWorkOrder = null;
  export let isEdit = false;
  export let initialProject = null;
  export let initialWorker = null;

  const dispatch = createEventDispatcher();

  let formData = {
    title: existingWorkOrder?.title || '',
    project: existingWorkOrder?.project || initialProject || '',
    description: existingWorkOrder?.description || '',
    priority: existingWorkOrder?.priority || 'MEDIUM',
    status: existingWorkOrder?.status || 'IN_PROGRESS',
    assignedWorker: initialWorker || ''
  };

  let projects = [];
  let loading = false;
  let error = '';

  const priorityOptions = [
    { value: 'LOW', label: 'Low' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HIGH', label: 'High' },
    { value: 'URGENT', label: 'Urgent' }
  ];

  const statusOptions = [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'ON_HOLD', label: 'On Hold' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  $: workers = $worker.workers || [];

  onMount(async () => {
    try {
      await projectStore.load();
      projects = $projectStore.items || [];
      await worker.loadWorkers();
    } catch (e) {
      // Failed to load data
    }
  });

  async function handleSubmit() {
    if (!formData.title.trim()) {
      error = 'Title is required';
      return;
    }
    if (!formData.project) {
      error = 'Project is required';
      return;
    }

    loading = true;
    error = '';

    try {
      const data = {
        title: formData.title.trim(),
        project: formData.project,
        description: formData.description.trim() || '',
        priority: formData.priority,
        status: formData.status
      };

      if (formData.assignedWorker) {
        data.assigned_workers = [{ worker_id: formData.assignedWorker }];
      }

      if (isEdit && existingWorkOrder?.id) {
        await workOrderStore.update(existingWorkOrder.id, data);
      } else {
        await workOrderStore.add(data);
      }
      dispatch('close');
    } catch (e) {
      error = e.message || 'Failed to save';
    } finally {
      loading = false;
    }
  }
</script>

<div class="form-container">
  <div class="form-header">
    <h2>{isEdit ? 'Edit Work Order' : 'New Work Order'}</h2>
    <button class="btn btn-secondary" on:click={() => dispatch('close')}>Cancel</button>
  </div>

  {#if error}
    <div class="form-error">{error}</div>
  {/if}

  <form on:submit|preventDefault={handleSubmit}>
    <div class="form-group">
      <label for="title">Title <span class="required">*</span></label>
      <input id="title" type="text" bind:value={formData.title} placeholder="Work order title" required />
    </div>

    <div class="form-group">
      <label for="project">Project <span class="required">*</span></label>
      <select id="project" bind:value={formData.project} required>
        <option value="">Select project...</option>
        {#each projects as p}
          <option value={p.id}>{p.title}</option>
        {/each}
      </select>
    </div>

    <div class="form-group">
      <label for="description">Description</label>
      <textarea id="description" bind:value={formData.description} rows="4" placeholder="Work order details..."></textarea>
    </div>

    <div class="form-group">
      <label for="worker">Assign Worker</label>
      <select id="worker" bind:value={formData.assignedWorker}>
        <option value="">No worker assigned</option>
        {#each workers as w}
          <option value={w.id}>{w.name}</option>
        {/each}
      </select>
    </div>

    <div class="form-row">
      <div class="form-group">
        <label for="priority">Priority</label>
        <select id="priority" bind:value={formData.priority}>
          {#each priorityOptions as opt}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>
      </div>
      <div class="form-group">
        <label for="status">Status</label>
        <select id="status" bind:value={formData.status}>
          {#each statusOptions as opt}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>
      </div>
    </div>

    <div class="form-actions">
      <button type="submit" class="btn btn-primary" disabled={loading}>
        {loading ? 'Saving...' : 'Save Work Order'}
      </button>
    </div>
  </form>
</div>

<style>
  .form-container {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }

  .form-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
  }

  .form-header h2 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  .form-error {
    margin: 16px 20px 0;
    padding: 12px;
    background: #fee2e2;
    color: #dc2626;
    border-radius: var(--radius);
    font-size: 13px;
  }

  form {
    padding: 20px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 6px;
    color: var(--text);
  }

  .required {
    color: var(--danger);
  }

  input, select, textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    font-size: 14px;
    background: #fff;
  }

  input:focus, select:focus, textarea:focus {
    outline: none;
    border-color: var(--accent);
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }

  .form-actions {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }

  @media (max-width: 640px) {
    .form-row {
      grid-template-columns: 1fr;
    }
  }
</style>
